import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const AdminContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: var(--color-gray-50);
`;

const Sidebar = styled.aside<{ isOpen: boolean }>`
  width: ${props => props.isOpen ? '280px' : '80px'};
  background: var(--color-white);
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease;
  position: fixed;
  height: 100vh;
  z-index: 1000;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: ${props => props.isOpen ? '100vw' : '0'};
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  }
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.div<{ isCollapsed: boolean }>`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-gray-800);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  span {
    display: ${props => props.isCollapsed ? 'none' : 'inline'};
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--border-radius-md);
  transition: background-color 0.2s ease;

  &:hover {
    background: var(--color-gray-100);
  }
`;

const Navigation = styled.nav`
  padding: 1rem 0;
`;

const NavSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3<{ isCollapsed: boolean }>`
  padding: 0 1.5rem;
  margin: 0 0 0.5rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-gray-500);
  display: ${props => props.isCollapsed ? 'none' : 'block'};
`;

const NavLink = styled(Link)<{ $isActive: boolean; $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  color: ${props => props.$isActive ? 'var(--color-primary)' : 'var(--color-gray-700)'};
  text-decoration: none;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  background: ${props => props.$isActive ? 'var(--color-primary-50)' : 'transparent'};
  border-right: ${props => props.$isActive ? '3px solid var(--color-primary)' : '3px solid transparent'};
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: var(--color-gray-100);
    color: var(--color-gray-900);
  }
  
  .icon {
    flex-shrink: 0;
    font-size: 1.25rem;
  }
  
  .label {
    display: ${props => props.$isCollapsed ? 'none' : 'inline'};
  }
`;

const MainContent = styled.main<{ sidebarOpen: boolean }>`
  flex: 1;
  margin-left: ${props => props.sidebarOpen ? '280px' : '80px'};
  transition: margin-left 0.3s ease;
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Header = styled.header`
  background: var(--color-white);
  padding: 1rem 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-800);
  margin: 0;
`;


const ContentArea = styled.div`
  padding: 2rem;
`;

const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const navigationItems = [
    {
      section: 'Overview',
      items: [
        { path: '/admin', icon: '📊', label: 'Dashboard' },
        { path: '/admin/analytics', icon: '📈', label: 'Analytics' },
      ]
    },
    {
      section: 'Content Management',
      items: [
        { path: '/admin/stories', icon: '📚', label: 'Story Queue' },
        { path: '/admin/appeals', icon: '📝', label: 'Appeal Management' },
        { path: '/admin/content', icon: '🛡️', label: 'Content Moderation' },
        { path: '/admin/reports', icon: '🚨', label: 'Reports' },
      ]
    },
    {
      section: 'User Management',
      items: [
        { path: '/admin/users', icon: '👥', label: 'Users' },
        { path: '/admin/moderation', icon: '⚖️', label: 'User Actions' },
      ]
    },
    {
      section: 'AI & Automation',
      items: [
        { path: '/admin/ai-settings', icon: '🤖', label: 'AI Settings' },
        { path: '/admin/settings', icon: '⚙️', label: 'Settings' },
      ]
    }
  ];

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/admin') return 'Admin Dashboard';
    if (path.includes('/analytics')) return 'Analytics Dashboard';
    if (path.includes('/users')) return 'User Management';
    if (path.includes('/content')) return 'Content Moderation';
    if (path.includes('/stories')) return 'Story Approval Queue';
    if (path.includes('/appeals')) return 'Appeal Management';
    if (path.includes('/reports')) return 'Analytics & Reports';
    if (path.includes('/ai-settings')) return 'AI Configuration';
    return 'Admin Panel';
  };

  return (
    <AdminContainer>
      <Sidebar isOpen={sidebarOpen}>
        <SidebarHeader>
          <Logo isCollapsed={!sidebarOpen}>
            <span role="img" aria-label="admin">🛠️</span>
            <span>Admin Panel</span>
          </Logo>
          <ToggleButton onClick={toggleSidebar}>
            {sidebarOpen ? '←' : '→'}
          </ToggleButton>
        </SidebarHeader>
        
        <Navigation>
          {navigationItems.map(section => (
            <NavSection key={section.section}>
              <SectionTitle isCollapsed={!sidebarOpen}>
                {section.section}
              </SectionTitle>
              {section.items.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  $isActive={location.pathname === item.path}
                  $isCollapsed={!sidebarOpen}
                >
                  <span className="icon">{item.icon}</span>
                  <span className="label">{item.label}</span>
                </NavLink>
              ))}
            </NavSection>
          ))}
        </Navigation>
      </Sidebar>

      <MainContent sidebarOpen={sidebarOpen}>
        <Header>
          {/* Admin header - navigation elements removed */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <MobileMenuButton onClick={toggleSidebar}>
              ☰
            </MobileMenuButton>
            <HeaderTitle>{getPageTitle()}</HeaderTitle>
          </div>
        </Header>
        
        <ContentArea>
          <Outlet />
        </ContentArea>
      </MainContent>
    </AdminContainer>
  );
};

export default AdminLayout;